import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface CollectionFilters {
  search: string
  brandId: string
  humidorId: string
  status: string
  sortBy: 'brand' | 'purchaseDate' | 'purchasePrice' | 'createdAt'
  sortOrder: 'asc' | 'desc'
}

export interface CollectionView {
  mode: 'grid' | 'list'
  showFilters: boolean
  showStats: boolean
  page: number
  limit: number
}

export interface CollectionStats {
  totalCigars: number
  availableCigars: number
  totalValue: number
  averagePrice: number
  brandCount: number
  humidorCount: number
}

export interface CollectionState {
  filters: CollectionFilters
  view: CollectionView
  stats: CollectionStats | null
  selectedCigars: string[]
  isBulkMode: boolean
  lastUpdated: string | null
}

const initialState: CollectionState = {
  filters: {
    search: '',
    brandId: '',
    humidorId: '',
    status: '',
    sortBy: 'brand',
    sortOrder: 'asc',
  },
  view: {
    mode: 'grid',
    showFilters: false,
    showStats: false,
    page: 1,
    limit: 12,
  },
  stats: null,
  selectedCigars: [],
  isBulkMode: false,
  lastUpdated: null,
}

const collectionSlice = createSlice({
  name: 'collection',
  initialState,
  reducers: {
    // Filter actions
    setSearch: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload
      state.view.page = 1 // Reset to first page when searching
    },
    setBrandFilter: (state, action: PayloadAction<string>) => {
      state.filters.brandId = action.payload
      state.view.page = 1
    },
    setHumidorFilter: (state, action: PayloadAction<string>) => {
      state.filters.humidorId = action.payload
      state.view.page = 1
    },
    setStatusFilter: (state, action: PayloadAction<string>) => {
      state.filters.status = action.payload
      state.view.page = 1
    },
    setSortBy: (state, action: PayloadAction<CollectionFilters['sortBy']>) => {
      state.filters.sortBy = action.payload
      state.view.page = 1
    },
    setSortOrder: (state, action: PayloadAction<CollectionFilters['sortOrder']>) => {
      state.filters.sortOrder = action.payload
      state.view.page = 1
    },
    clearFilters: (state) => {
      state.filters = initialState.filters
      state.view.page = 1
    },

    // View actions
    setViewMode: (state, action: PayloadAction<'grid' | 'list'>) => {
      state.view.mode = action.payload
    },
    toggleFilters: (state) => {
      state.view.showFilters = !state.view.showFilters
    },
    toggleStats: (state) => {
      state.view.showStats = !state.view.showStats
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.view.page = action.payload
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.view.limit = action.payload
      state.view.page = 1 // Reset to first page when changing limit
    },

    // Stats actions
    setStats: (state, action: PayloadAction<CollectionStats>) => {
      state.stats = action.payload
      state.lastUpdated = new Date().toISOString()
    },
    clearStats: (state) => {
      state.stats = null
      state.lastUpdated = null
    },

    // Selection actions
    selectCigar: (state, action: PayloadAction<string>) => {
      if (!state.selectedCigars.includes(action.payload)) {
        state.selectedCigars.push(action.payload)
      }
    },
    deselectCigar: (state, action: PayloadAction<string>) => {
      state.selectedCigars = state.selectedCigars.filter(id => id !== action.payload)
    },
    selectAllCigars: (state, action: PayloadAction<string[]>) => {
      state.selectedCigars = action.payload
    },
    clearSelection: (state) => {
      state.selectedCigars = []
      state.isBulkMode = false
    },
    toggleBulkMode: (state) => {
      state.isBulkMode = !state.isBulkMode
      if (!state.isBulkMode) {
        state.selectedCigars = []
      }
    },

    // Reset actions
    resetCollection: () => {
      return initialState
    },
  },
})

export const {
  setSearch,
  setBrandFilter,
  setHumidorFilter,
  setStatusFilter,
  setSortBy,
  setSortOrder,
  clearFilters,
  setViewMode,
  toggleFilters,
  toggleStats,
  setPage,
  setLimit,
  setStats,
  clearStats,
  selectCigar,
  deselectCigar,
  selectAllCigars,
  clearSelection,
  toggleBulkMode,
  resetCollection,
} = collectionSlice.actions

export default collectionSlice.reducer
